import React, { useState, useEffect } from 'react';
import { X, Check, AlertCircle, Users, Briefcase, Loader2 } from 'lucide-react';

const ApplyJobModal = ({ job, onClose, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsReferral, setNeedsReferral] = useState(true);
  const [potentialReferrers, setPotentialReferrers] = useState([]);
  const [selectedReferrer, setSelectedReferrer] = useState('');
  const [loadingReferrers, setLoadingReferrers] = useState(false);

  useEffect(() => {
    if (needsReferral) {
      fetchPotentialReferrers();
    }
  }, [needsReferral, job._id]);

  const fetchPotentialReferrers = async () => {
    try {
      setLoadingReferrers(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/jobs/${job._id}/potential-referrers`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch potential referrers');
      }

      const data = await response.json();
      setPotentialReferrers(data);
    } catch (err) {
      console.error('Error fetching referrers:', err);
      setError(err.message);
    } finally {
      setLoadingReferrers(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use the onSubmit prop from JobDetails component
      const applicationData = {
        applied: true,
        needsReferral,
        referrerId: needsReferral ? selectedReferrer : null
      };

      const result = await onSubmit(applicationData);
      
      if (result?.success) {
        onClose();
      } else {
        throw new Error(result?.message || 'Failed to submit application');
      }
    } catch (err) {
      setError(err.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg max-w-md w-full mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-medium">Apply for Position</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center">
                <AlertCircle className="h-4 w-4 mr-2" />
                <p>{error}</p>
              </div>
            )}

         

            {job.referralEnabled && (
              <div className="border-t pt-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={needsReferral}
                    onChange={(e) => {
                      setNeedsReferral(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedReferrer('');
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="text-sm text-gray-700">
                    I would like to request a referral
                  </span>
                </label>

                {needsReferral && (
                  <div className="mt-4 space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Select a Referrer
                    </label>
                    {loadingReferrers ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                      </div>
                    ) : potentialReferrers.length > 0 ? (
                      <select
                        value={selectedReferrer}
                        onChange={(e) => setSelectedReferrer(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        required={needsReferral}
                      >
                        <option value="">Select a referrer</option>
                        {potentialReferrers.map((referrer) => (
                          <option key={referrer._id} value={referrer._id}>
                            {referrer.firstName} {referrer.lastName} - {
                              referrer.professionalInfo.experiences.find(exp => exp.isCurrent)?.title || 'Current Employee'
                            }
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
                        No potential referrers found at {job.company.name}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mt-5 sm:mt-6 space-x-2 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || (needsReferral && !selectedReferrer)}
                className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:bg-blue-300"
              >
                {loading ? (
                  <span className="flex items-center">
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Applying...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Check className="h-4 w-4 mr-2" />
                    Apply Now
                  </span>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplyJobModal;